import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SecondApi } from "../api/secondApi";
import { getSecondApiLocale } from "../helper/apiLocale";

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB
const ALLOWED_EXT = [".pdf", ".doc", ".docx"];

function pickErrorMessage(data, fallback) {
  if (!data) return fallback;
  if (typeof data.message === "string" && data.message.trim()) {
    return data.message;
  }
  if (data.errors && typeof data.errors === "object") {
    for (const v of Object.values(data.errors)) {
      const arr = Array.isArray(v) ? v : [v];
      const first = arr.find((x) => typeof x === "string" && x.trim());
      if (first) return first;
    }
  }
  return fallback;
}

/** Convert a stored backend path like "uploads/x.pdf" to a full URL. */
function toAbsoluteUrl(pathOrUrl) {
  if (!pathOrUrl) return "";
  if (/^https?:\/\//i.test(pathOrUrl)) return pathOrUrl;
  const base = (process.env.REACT_APP_SECOND_API_KEY || "").replace(/\/$/, "");
  const path = String(pathOrUrl).replace(/^\//, "");
  return base ? `${base}/${path}` : `/${path}`;
}

/** Suggest a sensible filename from the URL (fallback to a default). */
function filenameFromUrl(url, fallback = "partner-agreement") {
  try {
    const pathname = new URL(url, window.location.origin).pathname;
    const name = decodeURIComponent(pathname.split("/").pop() || "");
    return name || fallback;
  } catch {
    return fallback;
  }
}

function PartnerApplicationModal({ onSuccess }) {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(1);

  // ---- Step 1: document fetch ----
  const [docUrl, setDocUrl] = useState("");
  const [docLoading, setDocLoading] = useState(true);
  const [docError, setDocError] = useState(null);
  const [downloading, setDownloading] = useState(false);

  const fetchDocument = useCallback(async () => {
    setDocLoading(true);
    setDocError(null);
    const locale = getSecondApiLocale(i18n.language);
    try {
      const { data } = await SecondApi.get(
        `/api/${locale}/auth/getPartnerApplicationDocument`,
      );
      const path = data?.data || data?.url || data?.path || "";
      setDocUrl(toAbsoluteUrl(path));
    } catch (error) {
      const fallback =
        error.code === "ERR_NETWORK" || !error.response
          ? t("partner.errors.network")
          : t("partner.errors.documentGeneric");
      setDocError(pickErrorMessage(error.response?.data, fallback));
    } finally {
      setDocLoading(false);
    }
  }, [i18n.language, t]);

  useEffect(() => {
    if (step === 1) fetchDocument();
  }, [step, fetchDocument]);

  const handleDownload = useCallback(async () => {
    if (!docUrl || downloading) return;
    setDownloading(true);
    try {
      // Try to fetch via SecondApi first (carries Authorization/token headers
      // in case the backend protects /uploads). If CORS or auth blocks it,
      // we fall back to opening in a new tab.
      const response = await SecondApi.get(docUrl, {
        responseType: "blob",
        // Bypass the SecondApi `baseURL` because docUrl is already absolute.
        baseURL: "",
      });
      const blob = response.data;
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filenameFromUrl(docUrl);
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke to ensure the download is initiated first.
      setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
    } catch (error) {
      // Fall back to opening the file in a new tab (lets the browser handle it).
      try {
        window.open(docUrl, "_blank", "noreferrer,noopener");
      } catch {
        /* swallow */
      }
    } finally {
      setDownloading(false);
    }
  }, [docUrl, downloading]);

  // ---- Step 2: application form ----
  const [submitMessage, setSubmitMessage] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!submitMessage || submitMessage.status) return;
    const timer = setTimeout(() => setSubmitMessage(null), 6000);
    return () => clearTimeout(timer);
  }, [submitMessage]);

  const validationSchema = Yup.object({
    type: Yup.string()
      .oneOf(["0", "1"])
      .required(t("partner.form.errors.typeRequired")),
    address: Yup.string().trim().max(255),
    company_name: Yup.string().when("type", {
      is: "1",
      then: (s) =>
        s
          .trim()
          .required(t("partner.form.errors.companyRequired"))
          .max(255),
      otherwise: (s) => s.trim().max(255),
    }),
    country: Yup.string().when("type", {
      is: "1",
      then: (s) => s.trim().required(t("partner.form.errors.countryRequired")),
      otherwise: (s) => s.trim(),
    }),
    city: Yup.string().when("type", {
      is: "1",
      then: (s) => s.trim().required(t("partner.form.errors.cityRequired")),
      otherwise: (s) => s.trim(),
    }),
    sign: Yup.mixed()
      .required(t("partner.form.errors.signRequired"))
      .test("size", t("partner.form.errors.signSize"), (file) =>
        file ? file.size <= MAX_FILE_SIZE : false,
      )
      .test("type", t("partner.form.errors.signType"), (file) => {
        if (!file) return false;
        const name = (file.name || "").toLowerCase();
        return ALLOWED_EXT.some((ext) => name.endsWith(ext));
      }),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const locale = getSecondApiLocale(i18n.language);
    setSubmitMessage(null);

    const fd = new FormData();
    fd.append("type", values.type);
    if (values.address) fd.append("address", values.address.trim());
    if (values.type === "1") {
      fd.append("company_name", values.company_name.trim());
      fd.append("country", values.country.trim());
      fd.append("city", values.city.trim());
    }
    fd.append("sign", values.sign);

    try {
      const { data } = await SecondApi.post(
        `/api/${locale}/auth/partnerApplications`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      const ok =
        data?.isSuccess === true ||
        data?.success === true ||
        (typeof data?.message === "string" &&
          /success|submitted|sent|applied/i.test(data.message));

      if (ok) {
        setSubmitMessage({
          status: true,
          text:
            (data && typeof data.message === "string" && data.message.trim()) ||
            t("partner.form.success"),
        });
        resetForm();
        if (typeof onSuccess === "function") {
          setTimeout(() => onSuccess(), 1200);
        }
      } else {
        setSubmitMessage({
          status: false,
          text: pickErrorMessage(data, t("partner.form.errors.generic")),
        });
      }
    } catch (error) {
      const fallback =
        error.code === "ERR_NETWORK" || !error.response
          ? t("partner.errors.network")
          : t("partner.form.errors.generic");
      setSubmitMessage({
        status: false,
        text: pickErrorMessage(error.response?.data, fallback),
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 1) {
    return (
      <div className="row gy-4">
        {/* Hero intro — product + partnership pitch */}
        <div className="col-12">
          <div
            className="rounded-4 p-4"
            style={{
              background:
                "linear-gradient(135deg, hsl(var(--main) / 0.10), hsl(var(--main) / 0.02))",
              border: "1px solid hsl(var(--main) / 0.18)",
            }}
          >
            <div className="d-flex align-items-start gap-3">
              <span
                className="d-inline-flex align-items-center justify-content-center text-white flex-shrink-0"
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 14,
                  background: "var(--main-gradient)",
                  boxShadow: "0 8px 20px hsl(var(--main) / 0.28)",
                }}
                aria-hidden="true"
              >
                <i className="las la-handshake" style={{ fontSize: "2rem" }} />
              </span>
              <div className="min-w-0">
                <h5 className="font-22 fw-700 mb-2">
                  {t("partner.step1.intro.title")}
                </h5>
                <p className="font-14 text-body mb-0">
                  {t("partner.step1.intro.product")}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Benefits grid */}
        <div className="col-12">
          <div
            className="font-12 text-uppercase fw-700 mb-3"
            style={{
              letterSpacing: "0.08em",
              color: "hsl(var(--main))",
            }}
          >
            <i className="las la-gift me-1" />
            {t("partner.step1.benefits.title")}
          </div>
          <div className="row gy-2">
            <BenefitItem
              icon="la-percentage"
              title={t("partner.step1.benefits.commission.title")}
              hint={t("partner.step1.benefits.commission.hint")}
            />
            <BenefitItem
              icon="la-link"
              title={t("partner.step1.benefits.referral.title")}
              hint={t("partner.step1.benefits.referral.hint")}
            />
            <BenefitItem
              icon="la-tachometer-alt"
              title={t("partner.step1.benefits.dashboard.title")}
              hint={t("partner.step1.benefits.dashboard.hint")}
            />
            <BenefitItem
              icon="la-tools"
              title={t("partner.step1.benefits.materials.title")}
              hint={t("partner.step1.benefits.materials.hint")}
            />
            <BenefitItem
              icon="la-headset"
              title={t("partner.step1.benefits.support.title")}
              hint={t("partner.step1.benefits.support.hint")}
            />
            <BenefitItem
              icon="la-medal"
              title={t("partner.step1.benefits.tiers.title")}
              hint={t("partner.step1.benefits.tiers.hint")}
            />
          </div>
        </div>

        {/* Process steps */}
        <div className="col-12">
          <div
            className="font-12 text-uppercase fw-700 mb-3"
            style={{
              letterSpacing: "0.08em",
              color: "hsl(var(--main))",
            }}
          >
            <i className="las la-route me-1" />
            {t("partner.step1.process.title")}
          </div>
          <div className="row gx-2 gy-2">
            <ProcessStep
              number={1}
              icon="la-file-download"
              title={t("partner.step1.process.step1")}
            />
            <ProcessStep
              number={2}
              icon="la-pen"
              title={t("partner.step1.process.step2")}
            />
            <ProcessStep
              number={3}
              icon="la-phone"
              title={t("partner.step1.process.step3")}
            />
            <ProcessStep
              number={4}
              icon="la-rocket"
              title={t("partner.step1.process.step4")}
              isLast
            />
          </div>
        </div>

        {/* Sample contract document download */}
        <div className="col-12">
          <div
            className="font-12 text-uppercase fw-700 mb-2"
            style={{
              letterSpacing: "0.08em",
              color: "hsl(var(--main))",
            }}
          >
            <i className="las la-file-contract me-1" />
            {t("partner.step1.title")}
          </div>
          <p className="text-body font-14 mb-3">
            {t("partner.step1.description")}
          </p>
          <div className="rounded-3 border p-3 d-flex flex-wrap align-items-center justify-content-between gap-3">
            <div className="d-flex align-items-center gap-3 min-w-0">
              <span
                className="d-inline-flex align-items-center justify-content-center bg-main bg-opacity-10 text-main rounded-circle flex-shrink-0"
                style={{ width: 48, height: 48 }}
                aria-hidden="true"
              >
                <i className="las la-file-pdf" style={{ fontSize: "1.6rem" }} />
              </span>
              <div className="min-w-0">
                <div className="font-16 fw-600 text-truncate">
                  {t("partner.step1.documentTitle")}
                </div>
                <div className="font-13 text-body">
                  {docLoading
                    ? t("partner.step1.documentLoading")
                    : docError
                    ? docError
                    : t("partner.step1.documentHint")}
                </div>
              </div>
            </div>

            {!docLoading && !docError && docUrl && (
              <button
                type="button"
                onClick={handleDownload}
                disabled={downloading}
                className="btn btn-outline-main pill fw-500"
              >
                {downloading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    />
                    {t("partner.step1.downloading", {
                      defaultValue: t("partner.step1.downloadBtn"),
                    })}
                  </>
                ) : (
                  <>
                    <i className="las la-download me-1" />
                    {t("partner.step1.downloadBtn")}
                  </>
                )}
              </button>
            )}

            {!docLoading && docError && (
              <button
                type="button"
                onClick={fetchDocument}
                className="btn btn-outline-main pill fw-500"
              >
                {t("partner.step1.retry")}
              </button>
            )}
          </div>
        </div>

        <div className="col-12">
          <button
            type="button"
            onClick={() => setStep(2)}
            className="btn btn-main btn-lg w-100 pill fw-500"
          >
            {t("partner.step1.continueBtn")}
            <i className="las la-arrow-right ms-1" />
          </button>
        </div>
      </div>
    );
  }

  // Step 2 — form
  return (
    <Formik
      initialValues={{
        type: "0",
        address: "",
        company_name: "",
        country: "",
        city: "",
        sign: null,
      }}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, isSubmitting }) => {
        const isLegal = values.type === "1";
        const selectedFile = values.sign;
        return (
          <Form className="row gy-4" autoComplete="off">
            <div className="col-12">
              <button
                type="button"
                onClick={() => setStep(1)}
                className="link text-main fw-500 bg-transparent border-0 p-0 font-14 d-inline-flex align-items-center gap-1"
              >
                <i className="las la-arrow-left" />
                {t("partner.step2.back")}
              </button>
            </div>

            <div className="col-12">
              <h5 className="mb-1 font-20 font-heading fw-600">
                {t("partner.step2.title")}
              </h5>
              <p className="text-body font-14 mb-0">
                {t("partner.step2.hint")}
              </p>
            </div>

            {submitMessage && (
              <div
                className={`col-12 font-14 ${
                  submitMessage.status ? "text-success" : "text-danger"
                }`}
                role="status"
              >
                {submitMessage.text}
              </div>
            )}

            <div className="col-12">
              <label className="form-label mb-2 font-18 font-heading fw-600">
                {t("partner.form.typeLabel")}
              </label>
              <div className="d-flex flex-wrap gap-3">
                <label
                  className={`flex-grow-1 rounded-3 border p-3 d-flex align-items-start gap-2 cursor-pointer ${
                    values.type === "0" ? "border-main bg-opacity-10" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                >
                  <Field
                    type="radio"
                    name="type"
                    value="0"
                    className="form-check-input mt-1"
                  />
                  <div>
                    <div className="font-16 fw-600">
                      {t("partner.form.typePhysical")}
                    </div>
                    <div className="font-13 text-body">
                      {t("partner.form.typePhysicalHint")}
                    </div>
                  </div>
                </label>
                <label
                  className={`flex-grow-1 rounded-3 border p-3 d-flex align-items-start gap-2 ${
                    values.type === "1" ? "border-main bg-opacity-10" : ""
                  }`}
                  style={{ cursor: "pointer" }}
                >
                  <Field
                    type="radio"
                    name="type"
                    value="1"
                    className="form-check-input mt-1"
                  />
                  <div>
                    <div className="font-16 fw-600">
                      {t("partner.form.typeLegal")}
                    </div>
                    <div className="font-13 text-body">
                      {t("partner.form.typeLegalHint")}
                    </div>
                  </div>
                </label>
              </div>
              <ErrorMessage
                name="type"
                component="div"
                className="text-danger mt-1 font-14"
              />
            </div>

            {isLegal && (
              <>
                <div className="col-md-6">
                  <label className="form-label mb-2 font-18 font-heading fw-600">
                    {t("partner.form.companyLabel")}
                  </label>
                  <Field
                    type="text"
                    name="company_name"
                    className="common-input border"
                    placeholder={t("partner.form.companyPlaceholder")}
                  />
                  <ErrorMessage
                    name="company_name"
                    component="div"
                    className="text-danger mt-1 font-14"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label mb-2 font-18 font-heading fw-600">
                    {t("partner.form.countryLabel")}
                  </label>
                  <Field
                    type="text"
                    name="country"
                    className="common-input border"
                    placeholder={t("partner.form.countryPlaceholder")}
                  />
                  <ErrorMessage
                    name="country"
                    component="div"
                    className="text-danger mt-1 font-14"
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label mb-2 font-18 font-heading fw-600">
                    {t("partner.form.cityLabel")}
                  </label>
                  <Field
                    type="text"
                    name="city"
                    className="common-input border"
                    placeholder={t("partner.form.cityPlaceholder")}
                  />
                  <ErrorMessage
                    name="city"
                    component="div"
                    className="text-danger mt-1 font-14"
                  />
                </div>
              </>
            )}

            <div className={isLegal ? "col-md-6" : "col-12"}>
              <label className="form-label mb-2 font-18 font-heading fw-600">
                {t("partner.form.addressLabel")}
              </label>
              <Field
                type="text"
                name="address"
                className="common-input border"
                placeholder={t("partner.form.addressPlaceholder")}
              />
              <ErrorMessage
                name="address"
                component="div"
                className="text-danger mt-1 font-14"
              />
            </div>

            <div className="col-12">
              <label className="form-label mb-2 font-18 font-heading fw-600">
                {t("partner.form.signLabel")}
              </label>
              <div className="rounded-3 border p-3 d-flex flex-wrap align-items-center gap-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                  onChange={(e) => {
                    const file = e.currentTarget.files?.[0] || null;
                    setFieldValue("sign", file);
                  }}
                  className="d-none"
                  id="partnerSignInput"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="btn btn-outline-main pill fw-500"
                >
                  <i className="las la-upload me-1" />
                  {selectedFile
                    ? t("partner.form.signReplace")
                    : t("partner.form.signChoose")}
                </button>
                <div className="flex-grow-1 min-w-0">
                  {selectedFile ? (
                    <div className="text-break">
                      <div className="font-15 fw-600 text-truncate">
                        {selectedFile.name}
                      </div>
                      <div className="font-12 text-body">
                        {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                      </div>
                    </div>
                  ) : (
                    <div className="font-13 text-body">
                      {t("partner.form.signHint")}
                    </div>
                  )}
                </div>
                {selectedFile && (
                  <button
                    type="button"
                    onClick={() => {
                      setFieldValue("sign", null);
                      if (fileInputRef.current) fileInputRef.current.value = "";
                    }}
                    className="link text-danger fw-500 bg-transparent border-0 p-0 font-14"
                    aria-label={t("partner.form.signRemove")}
                  >
                    <i className="las la-times-circle" style={{ fontSize: "1.4rem" }} />
                  </button>
                )}
              </div>
              <ErrorMessage
                name="sign"
                component="div"
                className="text-danger mt-1 font-14"
              />
            </div>

            <div className="col-12 d-flex flex-wrap gap-2 justify-content-end">
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn btn-main btn-lg pill fw-500"
              >
                {isSubmitting
                  ? t("partner.form.btnLoading")
                  : t("partner.form.btn")}
              </button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
}

function BenefitItem({ icon, title, hint }) {
  return (
    <div className="col-md-6 col-lg-4">
      <div
        className="rounded-3 p-3 h-100 d-flex align-items-start gap-3"
        style={{
          backgroundColor: "hsl(var(--section-bg, 210 14% 97%) / 0.6)",
          border: "1px solid hsl(var(--border-color, 220 13% 91%) / 0.6)",
        }}
      >
        <span
          className="d-inline-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
          style={{
            width: 40,
            height: 40,
            backgroundColor: "hsl(var(--main) / 0.12)",
            color: "hsl(var(--main))",
          }}
          aria-hidden="true"
        >
          <i className={`las ${icon}`} style={{ fontSize: "1.3rem" }} />
        </span>
        <div className="min-w-0">
          <div className="font-14 fw-600 mb-1">{title}</div>
          <div className="font-12 text-body">{hint}</div>
        </div>
      </div>
    </div>
  );
}

function ProcessStep({ number, icon, title, isLast }) {
  return (
    <div className="col-6 col-md-3">
      <div
        className="rounded-3 p-3 h-100 d-flex flex-column gap-2 position-relative"
        style={{
          backgroundColor: "hsl(var(--main) / 0.06)",
          border: "1px dashed hsl(var(--main) / 0.32)",
        }}
      >
        <div className="d-flex align-items-center gap-2">
          <span
            className="d-inline-flex align-items-center justify-content-center text-white fw-700 flex-shrink-0"
            style={{
              width: 28,
              height: 28,
              borderRadius: 8,
              background: "var(--main-gradient)",
              fontSize: 13,
            }}
          >
            {number}
          </span>
          <i
            className={`las ${icon}`}
            style={{ fontSize: "1.2rem", color: "hsl(var(--main))" }}
            aria-hidden="true"
          />
        </div>
        <div className="font-13 fw-600" style={{ color: "hsl(var(--heading-color))" }}>
          {title}
        </div>
        {!isLast && (
          <i
            className="las la-arrow-right d-none d-md-block position-absolute"
            style={{
              right: -12,
              top: "50%",
              transform: "translateY(-50%)",
              fontSize: "1.2rem",
              color: "hsl(var(--main) / 0.5)",
            }}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}

export default PartnerApplicationModal;
